import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios'
import { API_BASE_URL, STORAGE_KEYS, ERROR_MESSAGES } from '@/lib/constants'
import { ApiError, type ApiErrorResponse } from './types'
import { useAuthStore } from '@/stores/authStore'
import { safeLocalStorage } from '@/lib/safeStorage'
import { shouldAttemptTokenRefresh, type RetryableRequestConfig } from './refreshPolicy'
import {
  ACT_AS_HEADER,
  getActAsTarget,
  shouldAttachActAsHeader,
} from '@/features/facility-manager/actAs'

/**
 * axios 인스턴스
 * - baseURL: /api/v1 (Vite 프록시 -> Spring Boot)
 * - 요청 인터셉터: Authorization 헤더에 accessToken 주입
 * - 응답 인터셉터: 401 시 토큰 갱신 후 재시도
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

const isBrowser = typeof window !== 'undefined'

/** 세션 만료 처리 */
function handleSessionExpired() {
  if (!isBrowser) return
  safeLocalStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  safeLocalStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  safeLocalStorage.removeItem(STORAGE_KEYS.USER)
  // Zustand 메모리 상태 초기화
  useAuthStore.getState().logout()
  // 인증 필요 페이지에서는 로그인 페이지로 이동 + 안내
  const path = window.location.pathname
  const protectedPaths = ['/mypage', '/recommend', '/bookmarks', '/compare']
  if (protectedPaths.some((p) => path.startsWith(p))) {
    window.location.href = '/login'
  }
}

/** 토큰 갱신 중 여부 */
let isRefreshing = false

/** 토큰 갱신 대기 큐 */
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

/** 대기 큐 처리 */
function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else if (token) {
      resolve(token)
    }
  })
  failedQueue = []
}

/** 요청 인터셉터: accessToken 주입 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = isBrowser ? safeLocalStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) : null
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // 운영자 시설관리자 대신 처리 (docs/develop/facility/admin-act-as.md §6.2)
    if (isBrowser && config.headers) {
      const target = getActAsTarget()
      const role = useAuthStore.getState().user?.role
      if (shouldAttachActAsHeader(config.url, window.location.pathname, target, role) && target) {
        config.headers[ACT_AS_HEADER] = String(target.managerId)
      }
    }
    return config
  },
  (error: AxiosError) => Promise.reject(error)
)

/** 응답 인터셉터: 에러 핸들링 + 토큰 자동 갱신 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & RetryableRequestConfig)
      | undefined

    // 네트워크 에러
    if (!error.response) {
      throw new ApiError(
        ERROR_MESSAGES.NETWORK_ERROR,
        'NETWORK_ERROR',
        0
      )
    }

    const { status, data } = error.response

    // 토큰 갱신 요청 자체가 401 인 경우 -> 토큰 정리 (리다이렉트 안 함)
    if (status === 401 && originalRequest?.url?.includes('/auth/token/refresh')) {
      handleSessionExpired()
    }

    // 401 Unauthorized: 토큰 갱신 후 1회 재시도
    // - login/signup/oauth 의 401 은 자격 증명 오류이므로 갱신하지 않고 그대로 에러 처리
    // - 갱신 후 재시도한 요청(_retry)이 또 401 이면 다시 갱신하지 않음
    //   (2026-10-12 login 401 ↔ refresh 200 무한 루프 재발 방지, refreshPolicy.ts 참고)
    if (originalRequest && shouldAttemptTokenRefresh(status, originalRequest)) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              resolve(apiClient(originalRequest))
            },
            reject,
          })
        })
      }

      isRefreshing = true

      try {
        const refreshToken = isBrowser ? safeLocalStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) : null
        if (!refreshToken) {
          throw new Error('No refresh token')
        }

        const response = await axios.post(
          `${API_BASE_URL}/auth/token/refresh`,
          { refreshToken }
        )

        const { accessToken, refreshToken: newRefreshToken } = response.data.data
        if (isBrowser) {
          safeLocalStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken)
          safeLocalStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken)
        }

        processQueue(null, accessToken)

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError)
        // 토큰만 정리하고 현재 페이지에 머무름
        // 보호된 페이지는 RequireAuth 가드가 자동으로 로그인 페이지로 이동시킴
        handleSessionExpired()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // 비즈니스 에러 매핑
    const errorMessage = getErrorMessage(status, data)
    const errorCode = data?.error?.code ?? `HTTP_${status}`
    const errorDetails = data?.error?.details

    throw new ApiError(errorMessage, errorCode, status, errorDetails)
  }
)

/** HTTP 상태 코드별 에러 메시지 */
function getErrorMessage(status: number, data?: ApiErrorResponse): string {
  if (data?.error?.message) {
    return data.error.message
  }

  switch (status) {
    case 400:
      return ERROR_MESSAGES.VALIDATION_ERROR
    case 401:
      return ERROR_MESSAGES.UNAUTHORIZED
    case 403:
      return ERROR_MESSAGES.FORBIDDEN
    case 404:
      return ERROR_MESSAGES.NOT_FOUND
    case 500:
      return ERROR_MESSAGES.SERVER_ERROR
    default:
      return ERROR_MESSAGES.UNKNOWN_ERROR
  }
}

export default apiClient
